import React, { useState, useEffect } from "react";
import { Box, Grid, Slider, Stack, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import useOTBStyle from "../OTBStyle";
import FighterImage from "../../../assets/admin/OTB/Layer_1.svg";
import PoliceImage from "../../../assets/admin/OTB/police.svg";
import VSNTypography from "../../../shared/VSNTypography";

const Imaging = ({ data, pdf, otbInfo }) => {
  const classes = useOTBStyle();
  const ImageType = useSelector(
    (state) => state?.api?.OTBReportData?.department_info?.type
  );

  const visceralFatValues = data?.map((patient) => patient.visceral_fat);
  const numericValues = visceralFatValues.map(Number);
  const visceralMAX = Math.max(...numericValues) ?? 0;
  const visceralMIN = Math.min(...numericValues) ?? 0;
  const [visceralFat, setVisceralFat] = useState([visceralMIN, visceralMAX]);

  const BodyFatValues = data?.map((patient) => patient.body_fat_percentage);
  const bodyNumericValues = BodyFatValues.map(Number);
  const bodyFatMAX = Math.max(...bodyNumericValues) ?? 0;
  const bodyFatMIN = Math.min(...bodyNumericValues) ?? 0;
  const [bodyFat, setBodyFat] = useState([bodyFatMIN, bodyFatMAX]);

  useEffect(() => {
    setVisceralFat([visceralMIN, visceralMAX]);
  }, [visceralMIN, visceralMAX]);

  useEffect(() => {
    setBodyFat([bodyFatMIN, bodyFatMAX]);
  }, [bodyFatMIN, bodyFatMAX]);

  const handleChange1 = (event, newValue) => {
    setVisceralFat(newValue);
  };

  const handleChange2 = (event, newValue) => {
    setBodyFat(newValue);
  };

  const filterByVisceralFat = data.filter(
    (patient) =>
      patient.visceral_fat >= visceralFat[0] &&
      patient.visceral_fat <= visceralFat[1]
  );

  const filterByBodyFat = filterByVisceralFat.filter(
    (patient) =>
      patient.body_fat_percentage >= bodyFat[0] &&
      patient.body_fat_percentage <= bodyFat[1]
  );

  const abnormalData = [
    { label: "Thyroid", top: "22", left: "45", key: "thyroid_ab" },
    { label: "Aorta", top: "60", left: "48", key: "aorta_ab" },
    { label: "Liver", top: "50", left: "30", key: "liver_ab" },
    { label: "Gallbladder", top: "56", left: "36", key: "gall_bladder_ab" },
    { label: "Spleen", top: "46", left: "65", key: "spleen_ab" },
    { label: "Pancreas", top: "61", left: "40", key: "pancreas_ab" },
    { label: "Renal", top: "66", left: "50", key: "renal_ab" },
    { label: "Bladder", top: "75", left: "50", key: "bladder_ab" },
    { label: "Genitourinary", top: "70", left: "65", key: "genitourinary_ab" },
  ];

  const clinicallySignificantData = [
    { label: "Thyroid", key: "thyroid_cs" },
    { label: "Aorta", key: "aorta_cs" },
    { label: "Liver", key: "liver_cs" },
    { label: "Gallbladder", key: "gall_bladder_cs" },
    { label: "Spleen", key: "spleen_cs" },
    { label: "Pancreas", key: "pancreas_cs" },
    { label: "Renal", key: "renal_cs" },
    { label: "Bladder", key: "bladder_cs" },
    { label: "Genitourinary", key: "genitourinary_cs" },
  ];

  const abnormalXrayData = [
    {
      label: "Cervical Spine",
      top: "30",
      left: "50",
      key: "cervical_spine_ab",
    },
    { label: "Chest", top: "40", left: "40", key: "chest_ab" },
    {
      label: "Thoracic Spine",
      top: "50",
      left: "50",
      key: "thoracic_spine_ab",
    },
    { label: "Lumbar Spine", top: "68", left: "50", key: "lumbar_spine_ab" },
  ];

  const clinicallySignificantXrayData = [
    { label: "Cervical Spine", key: "cervical_spine_cs" },
    { label: "Chest", key: "chest_cs" },
    { label: "Thoracic Spine", key: "thoracic_spine_cs" },
    { label: "Lumbar Spine", key: "lumbar_spine_cs" },
  ];

  const countTrueValues = (key) => {
    return filterByBodyFat?.filter((item) => item[key] === 1).length;
  };

  const calculatePercentage = (abnormalValue, clinicallySignificantValue) => {
    const totalValue = abnormalValue + clinicallySignificantValue;
    if (totalValue === 0) {
      return 50;
    }
    return (abnormalValue / totalValue) * 100;
  };

  return (
    <Box>
      {pdf && (
        <Box
          className={classes.pdfReportLayout}
          sx={{ pageBreakAfter: "always" }}
        >
          <VSNTypography variant="h4">Imaging</VSNTypography>
          <Stack direction="row" marginTop={8}>
            <Stack
              direction="row"
              alignItems="center"
              pr={2}
              sx={{ borderRight: "1px solid #898994" }}
            >
              <Box>
                <VSNTypography fontWeight="bold" mb={3}>
                  Abnormal
                </VSNTypography>
                <Box pl={1} sx={{ borderLeft: "6px solid #F0C95C" }}>
                  {abnormalData?.map((item, index) => {
                    const abnormalCount = countTrueValues(item.key);
                    return (
                      <Stack mb={1} key={index}>
                        <VSNTypography fontWeight="bold">
                          {abnormalCount}
                        </VSNTypography>
                        <VSNTypography>{item.label}</VSNTypography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>

              <Box m={2} style={{ position: "relative" }}>
                <img
                  src={ImageType === "Fire" ? FighterImage : PoliceImage}
                  alt="Fighter"
                  height="300px"
                  width="200px"
                />

                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 4,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  <defs>
                    <linearGradient
                      id="circleGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="50%" stopColor="#F0C95C" />
                      <stop offset="50%" stopColor="#F54346" />
                    </linearGradient>
                  </defs>

                  {abnormalData.map((item, index) => {
                    const clinicallySignificantItem =
                      clinicallySignificantData.find(
                        (clinItem) => clinItem.label === item.label
                      );
                    const abnormalValue = countTrueValues(item.key);
                    const clinicallySignificantValue = clinicallySignificantItem
                      ? countTrueValues(clinicallySignificantItem.key)
                      : 0;
                    const percentage = calculatePercentage(
                      abnormalValue,
                      clinicallySignificantValue
                    );

                    const maxSize = 40;
                    const minSize = 8;
                    const size =
                      minSize + ((maxSize - minSize) * abnormalValue) / 100;

                    return (
                      <circle
                        key={index}
                        cx={`${item.left}%`}
                        cy={`${item.top}%`}
                        r={size / 2}
                        fill="url(#circleGradient)"
                        stroke="black"
                        strokeWidth="0"
                      />
                    );
                  })}
                </svg>
              </Box>

              <Box>
                <VSNTypography fontWeight="bold" textAlign="right" mb={3}>
                  Clinically Significant
                </VSNTypography>
                <Box pr={1} sx={{ borderRight: "6px solid #F54346" }}>
                  {clinicallySignificantData?.map((item, index) => {
                    const clinicallySignificantCount = countTrueValues(
                      item.key
                    );
                    return (
                      <Stack mb={1} key={index}>
                        <VSNTypography fontWeight="bold" textAlign="right">
                          {clinicallySignificantCount}
                        </VSNTypography>
                        <VSNTypography textAlign="right">
                          {item.label}
                        </VSNTypography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" pl={2}>
              <Box>
                <VSNTypography fontWeight="bold" mb={3}>
                  Abnormal
                </VSNTypography>
                <Box pl={1} sx={{ borderLeft: "6px solid #F0C95C" }}>
                  {abnormalXrayData?.map((item, index) => {
                    const abnormalCount = countTrueValues(item.key);
                    return (
                      <Stack mb={1} key={index}>
                        <VSNTypography fontWeight="bold">
                          {abnormalCount}
                        </VSNTypography>
                        <VSNTypography>{item.label}</VSNTypography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>

              <Box m={2} style={{ position: "relative" }}>
                <img
                  src={ImageType === "Fire" ? FighterImage : PoliceImage}
                  alt="Fighter"
                  height="300px"
                  width="200px"
                />

                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 4,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  <defs>
                    <linearGradient
                      id="circleGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="50%" stopColor="#F0C95C" />
                      <stop offset="50%" stopColor="#F54346" />
                    </linearGradient>
                  </defs>

                  {abnormalXrayData.map((item, index) => {
                    const clinicallySignificantItem =
                      clinicallySignificantData.find(
                        (clinItem) => clinItem.label === item.label
                      );
                    const abnormalValue = countTrueValues(item.key);
                    const clinicallySignificantValue = clinicallySignificantItem
                      ? countTrueValues(clinicallySignificantItem.key)
                      : 0;
                    const percentage = calculatePercentage(
                      abnormalValue,
                      clinicallySignificantValue
                    );

                    const maxSize = 40;
                    const minSize = 8;
                    const size =
                      minSize + ((maxSize - minSize) * abnormalValue) / 100;

                    return (
                      <circle
                        key={index}
                        cx={`${item.left}%`}
                        cy={`${item.top}%`}
                        r={size / 2}
                        fill="url(#circleGradient)"
                        stroke="black"
                        strokeWidth="0"
                      />
                    );
                  })}
                </svg>
              </Box>

              <Box>
                <VSNTypography fontWeight="bold" mb={3} textAlign="right">
                  Clinically Significant
                </VSNTypography>
                <Box pr={1} sx={{ borderRight: "6px solid #F54346" }}>
                  {clinicallySignificantXrayData?.map((item, index) => {
                    const clinicallySignificantCount = countTrueValues(
                      item.key
                    );
                    return (
                      <Stack mb={1} key={index}>
                        <VSNTypography fontWeight="bold" textAlign="right">
                          {clinicallySignificantCount}
                        </VSNTypography>
                        <VSNTypography textAlign="right">
                          {item.label}
                        </VSNTypography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
          </Stack>
        </Box>
      )}

      {!pdf && (
        <Box className={classes.reportLayout}>
          <div className={classes.stickyTitle}>
            <VSNTypography variant="h2">Imaging</VSNTypography>
          </div>
          <Stack
            direction={{ sm: "row" }}
            alignItems="center"
            justifyContent="end"
            spacing={3}
            sx={{ marginBottom: "32px" }}
          >
            <Stack
              spacing={2}
              sx={{
                backgroundColor: "#F1E1CD",
                borderRadius: "12px",
                maxWidth: { sm: 355 },
                width: "100%",
              }}
              p={2}
              mb={{ sm: 0, xs: 3 }}
            >
              <Stack
                direction="row"
                spacing={3}
                justifyContent="space-between"
                alignItems="center"
              >
                <Box flex={1}>
                  <VSNTypography
                    variant="h6"
                    sx={{
                      cursor: "pointer",
                      fontWeight: 700,
                      color: "#0A0A0B",
                      lineHeight: "31.5px",
                    }}
                  >
                    Visceral Fat
                  </VSNTypography>
                  <VSNTypography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#6E7079",
                      lineHeight: "23.44px",
                    }}
                  >
                    Low ({visceralMIN}) - High ({visceralMAX})
                  </VSNTypography>
                </Box>
                <VSNTypography
                  component="h4"
                  variant="h4"
                  sx={{ fontSize: "40px" }}
                >
                  {data?.filter(({ vitals }) => vitals === 1)?.length}
                </VSNTypography>
              </Stack>
              <Box>
                <Slider
                  value={visceralFat}
                  onChange={handleChange1}
                  valueLabelDisplay="auto"
                  color="natural"
                  min={visceralMIN}
                  max={visceralMAX}
                />
              </Box>
            </Stack>
            <Stack
              spacing={2}
              sx={{
                backgroundColor: "#F1E1CD",
                borderRadius: "12px",
                maxWidth: { sm: 355 },
                width: "100%",
              }}
              p={2}
            >
              <Stack
                direction="row"
                spacing={3}
                justifyContent="space-between"
                alignItems="center"
              >
                <Box flex={1}>
                  <VSNTypography
                    variant="h6"
                    sx={{
                      cursor: "pointer",
                      fontWeight: 700,
                      color: "#0A0A0B",
                      lineHeight: "31.5px",
                    }}
                  >
                    Body Fat %
                  </VSNTypography>
                  <VSNTypography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#6E7079",
                      lineHeight: "23.44px",
                    }}
                  >
                    Ideal: Male-15% Female-27%
                  </VSNTypography>
                </Box>
                <VSNTypography
                  component="h4"
                  variant="h4"
                  sx={{ fontSize: "40px" }}
                >
                  {data?.filter(({ vitals }) => vitals === 1)?.length}
                </VSNTypography>
              </Stack>
              <Box>
                <Slider
                  value={bodyFat}
                  onChange={handleChange2}
                  valueLabelDisplay="auto"
                  color="natural"
                  min={bodyFatMIN}
                  max={bodyFatMAX}
                />
              </Box>
            </Stack>
          </Stack>
          <Grid container flex={1}>
            <Grid item md={12} lg={6}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  borderRight: { lg: "1px solid #898994" },
                  pr: { lg: "3%" },
                  height: "100%",
                }}
              >
                <Box sx={{ width: "25%", flex: "0 0 25%" }}>
                  <VSNTypography fontWeight="bold" mb={3}>
                    Abnormal
                  </VSNTypography>
                  <Box pl={2} sx={{ borderLeft: "5px solid #F0C95C" }}>
                    {abnormalData?.map((item, index) => {
                      const abnormalCount = countTrueValues(item.key);
                      return (
                        <Stack mb={{ xl: 2.5, xs: 1 }} key={index}>
                          <VSNTypography fontWeight="bold" lineHeight="1rem">
                            {abnormalCount}
                          </VSNTypography>
                          <VSNTypography className={classes.imagingLabel}>
                            {item.label}
                          </VSNTypography>
                        </Stack>
                      );
                    })}
                  </Box>
                </Box>

                <Box m={4} flex={1}>
                  <Box style={{ position: "relative", textAlign: "center" }}>
                    <img
                      src={ImageType === "Fire" ? FighterImage : PoliceImage}
                      alt="Fighter"
                    />
                    <Stack
                      className="position-absolute"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {abnormalData.map((item, index) => {
                        const clinicallySignificantItem =
                          clinicallySignificantData.find(
                            (clinItem) => clinItem.label === item.label
                          );
                        const abnormalValue = countTrueValues(item.key);
                        const clinicallySignificantValue =
                          clinicallySignificantItem
                            ? countTrueValues(clinicallySignificantItem.key)
                            : 0;
                        const percentage = calculatePercentage(
                          abnormalValue,
                          clinicallySignificantValue
                        );

                        const maxSize = 30;
                        const minSize = 10;
                        const size =
                          minSize + ((maxSize - minSize) * abnormalValue) / 100;

                        const positionStyles = {
                          position: "absolute",
                          top: `${item?.top}%`,
                          left: `${item?.left}%`,
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: "50%",
                          background:
                            abnormalValue === 0 &&
                            clinicallySignificantValue === 0
                              ? `conic-gradient(#F0C95C 50%, #F54346 50%)`
                              : `conic-gradient(
              #F0C95C ${percentage}%,
              #F54346 ${percentage}% 100%
            )`,
                        };

                        return (
                          <Tooltip key={index} title={item.label}>
                            <Stack style={positionStyles} />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>

                <Box sx={{ width: "25%", flex: "0 0 25%" }}>
                  <VSNTypography fontWeight="bold" mb={3}>
                    Clinically Significant
                  </VSNTypography>
                  <Box pr={2} sx={{ borderRight: "5px solid #F54346" }}>
                    {clinicallySignificantData?.map((item, index) => {
                      const clinicallySignificantCount = countTrueValues(
                        item.key
                      );
                      return (
                        <Stack mb={{ xl: 2.5, xs: 1 }} key={index}>
                          <VSNTypography fontWeight="bold" lineHeight="1rem">
                            {clinicallySignificantCount}
                          </VSNTypography>
                          <VSNTypography className={classes.imagingLabel}>
                            {item.label}
                          </VSNTypography>
                        </Stack>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Grid>

            <Grid item md={12} lg={6}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ pl: { lg: "3%" }, height: "100%" }}
              >
                <Box sx={{ width: "25%", flex: "0 0 25%" }}>
                  <VSNTypography fontWeight="bold" mb={3}>
                    Abnormal
                  </VSNTypography>
                  <Box pl={2} sx={{ borderLeft: "5px solid #F0C95C" }}>
                    {abnormalXrayData?.map((item, index) => {
                      const abnormalCount = countTrueValues(item.key);
                      return (
                        <Stack mb={{ xl: 2.5, xs: 1 }} key={index}>
                          <VSNTypography fontWeight="bold" lineHeight="1rem">
                            {abnormalCount}
                          </VSNTypography>
                          <VSNTypography className={classes.imagingLabel}>
                            {item.label}
                          </VSNTypography>
                        </Stack>
                      );
                    })}
                  </Box>
                </Box>

                <Box m={4} flex={1}>
                  <Box style={{ position: "relative", textAlign: "center" }}>
                    <img
                      src={ImageType === "Fire" ? FighterImage : PoliceImage}
                      alt="Fighter"
                    />
                    <Stack
                      className="position-absolute"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {abnormalXrayData.map((item, index) => {
                        const clinicallySignificantItem =
                          clinicallySignificantXrayData.find(
                            (clinItem) => clinItem.label === item.label
                          );
                        const abnormalValue = countTrueValues(item.key);
                        const clinicallySignificantValue =
                          clinicallySignificantItem
                            ? countTrueValues(clinicallySignificantItem.key)
                            : 0;
                        const percentage = calculatePercentage(
                          abnormalValue,
                          clinicallySignificantValue
                        );

                        const maxSize = 30;
                        const minSize = 10;
                        const size =
                          minSize + ((maxSize - minSize) * abnormalValue) / 100;

                        const positionStyles = {
                          position: "absolute",
                          top: `${item?.top}%`,
                          left: `${item?.left}%`,
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: "50%",
                          background:
                            abnormalValue === 0 &&
                            clinicallySignificantValue === 0
                              ? `conic-gradient(#F0C95C 50%, #F54346 50%)`
                              : `conic-gradient(
              #F0C95C ${percentage}%,
              #F54346 ${percentage}% 100%
            )`,
                        };

                        return (
                          <Tooltip key={index} title={item.label}>
                            <Stack style={positionStyles} />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>

                <Box sx={{ width: "25%", flex: "0 0 25%" }}>
                  <VSNTypography fontWeight="bold" mb={3}>
                    Clinically Significant
                  </VSNTypography>
                  <Box pr={2} sx={{ borderRight: "5px solid #F54346" }}>
                    {clinicallySignificantXrayData?.map((item, index) => {
                      const clinicallySignificantCount = countTrueValues(
                        item.key
                      );
                      return (
                        <Stack mb={{ xl: 2.5, xs: 1 }} key={index}>
                          <VSNTypography fontWeight="bold" lineHeight="1rem">
                            {clinicallySignificantCount}
                          </VSNTypography>
                          <VSNTypography className={classes.imagingLabel}>
                            {item.label}
                          </VSNTypography>
                        </Stack>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export const TwoColorDot = ({ percentage }) => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12">
      <circle cx="6" cy="6" r="6" fill="#F0C95C" />
      <path
        d={`M 6 0 A 6 6 0 0 1 ${
          6 + 6 * Math.cos((2 * Math.PI * percentage) / 100)
        } ${6 + 6 * Math.sin((2 * Math.PI * percentage) / 100)} L 6 6 Z`}
        fill="#F54346"
      />
    </svg>
  );
};

export default Imaging;
